/**
 * StationSelector Page
 * 
 * PURPOSE: Physical workstation selection and claiming interface
 * This is the FIRST STEP before operators can work on cars
 * 
 * BUSINESS RULES:
 * - 12 physical stations per production line
 * - One operator per station (enforced by unique constraint)
 * - One station per operator (enforced by unique constraint)
 * - Must claim station before checking out cars
 * - Real-time updates show station availability
 * 
 * LOCKING MECHANISM:
 * - station_claims table tracks active claims (released_at = NULL)
 * - Partial unique indexes prevent concurrent claims
 * - Database enforces constraints (app just handles UI)
 * 
 * FLOW:
 * 1. User sees 12 station cards (grid layout)
 * 2. Green = available, Red = occupied by X, Blue = mine
 * 3. Click to claim → insert into station_claims
 * 4. Database prevents duplicate claims (returns error)
 * 5. Real-time subscription updates other users' views
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useSiteRoles } from '@/hooks/useSiteRoles';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { canClaimStation as hasStationClaimAccess } from '@/lib/roleUtils';
import { CheckCircle2, User, Lock, ArrowRight, AlertTriangle } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useTranslation } from 'react-i18next';

interface Station {
  id: string;
  station_number: number;
  name: string | null;
  required_roles?: string[] | null;
  // Joined from active claim
  claimed_by?: string;
  claimed_by_name?: string;
}

interface StationSelectorProps {
  lineId?: string; // Optional - will get from context if not provided
}

const StationSelector = ({ lineId: propLineId }: StationSelectorProps) => {
  const { t } = useTranslation();
  const { user, currentLineId, currentTenantId } = useAuth();
  const lineId = propLineId || currentLineId;
  const { roles, isAdmin } = useSiteRoles(lineId || undefined);
  const navigate = useNavigate();
  const [stations, setStations] = useState<Station[]>([]);
  const [myClaimId, setMyClaimId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [claiming, setClaiming] = useState(false);
  const [forceClaimStation, setForceClaimStation] = useState<Station | null>(null);

  useEffect(() => {
    if (lineId && currentTenantId) {
      fetchStations();
      const cleanup = subscribeToStationClaims();
      return cleanup;
    }
  }, [lineId, currentTenantId]);

  const fetchStations = async () => {
    if (!lineId || !currentTenantId) {
      setStations([]);
      setLoading(false);
      return;
    }
    try {
      const { data: stationsData, error } = await ((supabase as any)
        .schema('mod_styling'))
        .from('stations')
        .select(`
          id,
          station_number,
          name,
          required_roles,
          station_claims!left (
            id,
            user_id
          )
        `)
        .eq('line_id', lineId)
        .eq('tenant_id', currentTenantId)
        .eq('active', true)
        .is('station_claims.released_at', null)
        .order('station_number');

      if (error) throw error;

      const claimUserIds = Array.from(new Set(
        (stationsData || [])
          .flatMap(station => station.station_claims || [])
          .map(claim => claim.user_id)
          .filter(Boolean)
      ));

      let profileMap = new Map<string, string | null>();

      if (claimUserIds.length > 0 && currentTenantId) {
        const { data: profiles } = await ((supabase as any)
          .schema('mod_styling'))
          .from('v_profiles')
          .select('id, full_name')
          .in('id', claimUserIds)
          .eq('tenant_id', currentTenantId);

        profileMap = new Map(
          (profiles || []).map((profile: any) => [profile.id, profile.full_name || null])
        );
      }

      const transformedStations: Station[] = (stationsData || []).map(station => {
        const activeClaim = station.station_claims?.[0];
        return {
          id: station.id,
          station_number: station.station_number,
          name: station.name,
          required_roles: station.required_roles,
          claimed_by: activeClaim?.user_id,
          claimed_by_name: activeClaim ? profileMap.get(activeClaim.user_id) || undefined : undefined,
        };
      });

      setStations(transformedStations);

      if (user && currentTenantId) {
        const { data: myClaim } = await ((supabase as any)
          .schema('mod_styling'))
          .from('station_claims')
          .select('id, station_id')
          .eq('user_id', user.id)
          .eq('tenant_id', currentTenantId)
          .is('released_at', null)
          .maybeSingle();

        setMyClaimId(myClaim?.station_id || null);
      }
    } catch (error) {
      console.error('Error fetching stations:', error);
      toast.error(t('stations.failedToLoad'));
    } finally {
      setLoading(false);
    }
  };

  const subscribeToStationClaims = () => {
      const channel = supabase
        .channel('station_claims_changes')
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'mod_styling',
            table: 'station_claims',
          },
        () => {
          fetchStations();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  };

  const handleClaimStation = async (stationId: string, force = false) => {
    if (!user || !currentTenantId || !lineId) return;

    console.log('Claiming station:', { stationId, force, userId: user.id });

    setClaiming(true);
    try {
      const now = new Date().toISOString();

      // Step 1: Release all claims on this station
      const { data: releasedStation, error: releaseStationError } = await ((supabase as any)
        .schema('mod_styling'))
        .from('station_claims')
        .update({ released_at: now })
        .eq('station_id', stationId)
        .eq('tenant_id', currentTenantId)
        .is('released_at', null)
        .select();

      if (releaseStationError) {
        console.error('Error releasing station:', releaseStationError);
        toast.error(`${t('stations.failedToRelease')}: ${releaseStationError.message}`);
        return;
      }

      // Step 2: Release any other claims by current user
      const { data: releasedMy, error: releaseError } = await ((supabase as any)
        .schema('mod_styling'))
        .from('station_claims')
        .update({ released_at: now })
        .eq('user_id', user.id)
        .eq('tenant_id', currentTenantId)
        .is('released_at', null)
        .neq('station_id', stationId)
        .select();

      if (releaseError) {
        console.error('Error releasing my claims:', releaseError);
      }

      // Step 3: Claim new station
      const { data: newClaim, error: claimError } = await ((supabase as any)
        .schema('mod_styling'))
        .from('station_claims')
        .insert({
          tenant_id: currentTenantId,
          station_id: stationId,
          user_id: user.id,
        })
        .select();

      if (claimError) {
        console.error('Error claiming:', claimError);
        toast.error(`${t('stations.failedToClaim')}: ${claimError.message || claimError.code || ''}`);
        fetchStations();
        return;
      }

      toast.success(force ? t('stations.stationTakenOver') : t('stations.stationClaimed'));
      setMyClaimId(stationId);
      fetchStations();

      setTimeout(() => navigate('/operator'), 500);
    } catch (error: any) {
      console.error('Error claiming station:', error);
      toast.error(error?.message ? `${t('stations.failedToClaim')}: ${error.message}` : t('stations.failedToClaim'));
    } finally {
      setClaiming(false);
    }
  };

  const handleForceClaim = async () => {
    if (!forceClaimStation) return;
    await handleClaimStation(forceClaimStation.id, true);
    setForceClaimStation(null);
  };

  const handleReleaseStation = async () => {
    if (!user || !myClaimId) return;

    try {
      const { error } = await ((supabase as any)
        .schema('mod_styling'))
        .from('station_claims')
        .update({ released_at: new Date().toISOString() })
        .eq('user_id', user.id)
        .eq('tenant_id', currentTenantId)
        .is('released_at', null);

      if (error) throw error;

      toast.success(t('stations.stationReleased'));
      setMyClaimId(null);
      fetchStations();
      setTimeout(() => navigate('/operator'), 500);
    } catch (error) {
      console.error('Error releasing station:', error);
      toast.error(t('stations.failedToRelease'));
    }
  };

  if (loading) {
    return <div className="p-8 text-center">{t('stations.loadingStations')}</div>;
  }

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="max-w-5xl mx-auto">
        {/* Compact header */}
        <div className="mb-5">
          <h1 className="text-2xl font-bold">{t('stations.selectWorkstation')}</h1>
          <p className="text-sm text-muted-foreground">{t('stations.claimToBegin')}</p>
        </div>

        {/* Station Grid — 4 columns, compact cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
          {stations.map((station) => {
            const isMine = myClaimId === station.id;
            const isOccupied = station.claimed_by && !isMine;
            const isAvailable = !station.claimed_by;
            const hasRequiredRole = hasStationClaimAccess(roles, station.required_roles, isAdmin);
            const canInteract = isAvailable && hasRequiredRole;

            return (
              <button
                key={station.id}
                disabled={claiming || (!canInteract && !isMine && !isOccupied)}
                onClick={() => {
                  if (isMine) {
                    handleReleaseStation();
                  } else if (canInteract && !claiming) {
                    handleClaimStation(station.id);
                  } else if (isAvailable && !hasRequiredRole) {
                    toast.error(t('stations.insufficientRole'));
                  } else if (isOccupied && hasRequiredRole) {
                    setForceClaimStation(station);
                  }
                }}
                className={cn(
                  'relative flex flex-col items-center gap-1.5 rounded-lg border p-3 transition-all text-center',
                  'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary',
                  isMine && 'bg-primary/10 border-primary ring-1 ring-primary/30',
                  isOccupied && 'bg-orange-50 dark:bg-orange-950/20 border-orange-300/50 dark:border-orange-700/40 cursor-pointer',
                  canInteract && 'hover:bg-green-50 dark:hover:bg-green-950/20 hover:border-green-400/50 dark:hover:border-green-700/40 cursor-pointer',
                  isAvailable && !hasRequiredRole && 'opacity-50 cursor-not-allowed',
                )}
              >
                {/* Station Number Circle — compact */}
                <div
                  className={cn(
                    'w-10 h-10 rounded-full flex items-center justify-center text-lg font-bold border',
                    isMine && 'bg-primary text-primary-foreground border-primary',
                    isOccupied && 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400 border-orange-300/50 dark:border-orange-700/40',
                    isAvailable && !isMine && 'bg-background text-foreground border-border',
                  )}
                >
                  {station.station_number}
                </div>

                {/* Station Name — single line, truncated */}
                <span className="text-xs font-medium truncate w-full">
                  {station.name || t('stations.stationLabel', { number: station.station_number })}
                </span>

                {/* Status line */}
                <span
                  className={cn(
                    'text-[10px] truncate w-full',
                    isMine && 'text-primary font-semibold',
                    isOccupied && 'text-orange-600 dark:text-orange-400',
                    isAvailable && !isMine && 'text-muted-foreground',
                  )}
                >
                  {isMine
                    ? t('stations.yourStation')
                    : isOccupied
                      ? station.claimed_by_name || t('stations.occupied')
                      : t('stations.available')}
                </span>

                {/* Role badges — compact inline */}
                {station.required_roles && station.required_roles.length > 0 && (
                  <div className="flex flex-wrap gap-0.5 justify-center">
                    {station.required_roles.map((role) => (
                      <Badge
                        key={role}
                        variant="outline"
                        className="text-[9px] h-4 px-1"
                      >
                        {t(`roles.${role}`, { defaultValue: role })}
                      </Badge>
                    ))}
                  </div>
                )}

                {/* Corner indicators */}
                {isMine && (
                  <div className="absolute top-1 right-1">
                    <CheckCircle2 className="h-3.5 w-3.5 text-primary" />
                  </div>
                )}
                {isOccupied && (
                  <div className="absolute top-1 right-1">
                    <Lock className="h-3 w-3 text-orange-500 dark:text-orange-400" />
                  </div>
                )}
              </button>
            );
          })}
        </div>

        {/* Continue Button (if station claimed) */}
        {myClaimId && (
          <div className="mt-6 flex justify-center">
            <Button size="lg" onClick={() => navigate('/operator')}>
              {t('stations.continueToVehicles')}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        )}

        {/* Help Text — compact */}
        <div className="mt-6 p-2.5 bg-muted/50 rounded text-[11px] text-muted-foreground">
          <div className="flex flex-wrap gap-x-4 gap-y-0.5">
            <span>• {t('stations.helpGreen')}</span>
            <span>• {t('stations.helpBlue')}</span>
            <span>• {t('stations.helpOrange')}</span>
          </div>
        </div>
      </div>

      {/* Force Claim Dialog */}
      <AlertDialog open={!!forceClaimStation} onOpenChange={() => setForceClaimStation(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              {t('stations.takeOverTitle')}
            </AlertDialogTitle>
            <AlertDialogDescription
              dangerouslySetInnerHTML={{
                __html: t('stations.takeOverDescription', {
                  number: forceClaimStation?.station_number,
                  name: forceClaimStation?.claimed_by_name || t('stations.anotherOperator'),
                }),
              }}
            />
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('stations.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleForceClaim}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t('stations.takeOverStation')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default StationSelector;
